import { Inject, Injectable, Optional } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';
import { FEATURE_API_CONFIG, FeatureApiConfig } from './feature-flags-api.config';
import { EvaluationContext, EvaluationResponse, FeatureFlag, FeatureFlagResponse } from '../models/feature-flag.model';
import { CbreLogger, CbreLoggerToken } from '@cbre/logger';

/**
 * Service to interact with the feature flags API
 */
@Injectable({
  providedIn: 'root',
})
export class FeatureFlagsApiService {
  private apiBaseUrl: string;
  private cachedFlags$: Observable<FeatureFlag[]>;

  constructor(
    private http: HttpClient,
    @Optional() @Inject(FEATURE_API_CONFIG) private config: FeatureApiConfig,
    @Inject(CbreLoggerToken) private logger: CbreLogger
  ) {
    this.apiBaseUrl = config?.apiBaseUrl || '/v1/feature-flags';
  }

  /**
   * Fetch all active feature flags
   */
  getAllFeatureFlags(): Observable<FeatureFlag[]> {
    // Use cached value if exists, otherwise fetch from API
    if (!this.cachedFlags$) {
      this.cachedFlags$ = this.http.get<FeatureFlagResponse>(`${this.apiBaseUrl}?status=active`).pipe(
        map(response => response.flags),
        catchError(error => {
          this.logger.error(`Error fetching feature flags: ${error?.message ?? error}`);
          return of([]);
        }),
        shareReplay(1)
      );
    }
    return this.cachedFlags$;
  }

  /**
   * Get a specific feature flag by ID
   */
  getFeatureFlag(id: string): Observable<FeatureFlag> {
    return this.http.get<FeatureFlag>(`${this.apiBaseUrl}/${id}`).pipe(
      catchError(error => {
        this.logger.error(`Error fetching feature flag ${id}: ${error?.message ?? error}`);
        return throwError(() => new Error(`Feature flag ${id} not found`));
      })
    );
  }

  /**
   * Evaluate a feature flag against a given context
   */
  evaluateFeatureFlag(id: string, context: EvaluationContext): Observable<boolean> {
    return this.http.post<EvaluationResponse>(`${this.apiBaseUrl}/${id}/evaluate`, { context }).pipe(
      map(response => response.enabled),
      catchError(error => {
        this.logger.error(`Error evaluating feature flag ${id}: ${error?.message ?? error}`);
        return of(false); // Default to disabled on error
      })
    );
  }

  /**
   * Invalidate the cache to force a refresh of feature flags
   */
  invalidateCache(): void {
    this.cachedFlags$ = null;
  }
}
